import { useState, useCallback, useEffect, useRef } from 'react';
import { useUnsavedChangesWarning } from '../../../hooks/useUnsavedChangesWarning';

const TEXT_FIELDS = [
  { field: 'gapAnalysis', label: 'Gap Between Candidates' },
  { field: 'evaluationProcess', label: 'Evaluation Process' },
  { field: 'integrityMatters', label: 'Integrity Matters & Disclosures' },
  { field: 'diversityIssues', label: 'Diversity Issues' },
  { field: 'fnsFeedback', label: 'Fellowship Nomination System (FNS)' },
  { field: 'generalObservations', label: 'General Observations / Feedback' },
];

const useChairReportForm = ({
  formData,
  setFormData,
  getFieldStatus,
  getCandidateStatus,
  getCandidateExpertiseStatus,
  getAttendanceStatus,
  isEditable,
  isEditing,
  committeeId,
  onDirtyChange,
}) => {
  const [validationErrors, setValidationErrors] = useState([]);
  const [warningErrors, setWarningErrors] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  useUnsavedChangesWarning(isDirty && isEditable);

  useEffect(() => {
    onDirtyChange?.(committeeId, isDirty);
  }, [isDirty, committeeId, onDirtyChange]);

  const prevIsEditingRef = useRef(isEditing);
  useEffect(() => {
    if (prevIsEditingRef.current && !isEditing && isDirty) {
      setIsDirty(false);
    }
    prevIsEditingRef.current = isEditing;
  }, [isEditing, isDirty]);

  const handleFieldChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setValidationErrors((prev) => prev.filter((e) => e.field !== field));
      setWarningErrors((prev) => prev.filter((e) => e.field !== field));
      setIsDirty(true);
    },
    [setFormData],
  );

  const handleAttendanceChange = useCallback(
    (index, value) => {
      setFormData((prev) => {
        const updated = [...prev.memberAttendance];
        updated[index] = { ...updated[index], attendance: value };
        return { ...prev, memberAttendance: updated };
      });
      setValidationErrors((prev) =>
        prev.filter((e) => e.field !== `attendance-${index}`),
      );
      setWarningErrors((prev) =>
        prev.filter((e) => e.field !== `attendance-${index}`),
      );
      setIsDirty(true);
    },
    [setFormData],
  );

  const handleCandidateReportChange = useCallback(
    (index, value) => {
      setFormData((prev) => {
        const updated = [...prev.candidateReports];
        updated[index] = { ...updated[index], report: value };
        return { ...prev, candidateReports: updated };
      });
      setValidationErrors((prev) =>
        prev.filter((e) => e.field !== `candidate-${index}`),
      );
      setWarningErrors((prev) =>
        prev.filter((e) => e.field !== `candidate-${index}`),
      );
      setIsDirty(true);
    },
    [setFormData],
  );

  const handleCandidateExpertiseChange = useCallback(
    (index, value) => {
      setFormData((prev) => {
        const updated = [...prev.candidateReports];
        updated[index] = { ...updated[index], fieldOfExpertise: value };
        return { ...prev, candidateReports: updated };
      });
      setValidationErrors((prev) =>
        prev.filter((e) => e.field !== `candidate-expertise-${index}`),
      );
      setIsDirty(true);
    },
    [setFormData],
  );

  const validateFilledFields = useCallback(() => {
    const errors = [];

    TEXT_FIELDS.forEach(({ field, label }) => {
      const status = getFieldStatus(field);
      if (status.over) {
        errors.push({
          field,
          message: `${label} exceeds the ${status.limit} word limit (${status.count} words).`,
        });
      }
    });

    formData.memberAttendance.forEach((member, index) => {
      const status = getAttendanceStatus(index);
      if (status.over) {
        errors.push({
          field: `attendance-${index}`,
          message: `Attendance for ${member.memberName} exceeds the ${status.limit} word limit (${status.count} words).`,
          group: 'attendance',
        });
      }
    });

    formData.candidateReports.forEach((cr, index) => {
      const status = getCandidateStatus(index);
      if (status.over) {
        errors.push({
          field: `candidate-${index}`,
          message: `Report for ${cr.candidateName} exceeds the ${status.limit} word limit (${status.count} words).`,
          group: 'candidates',
        });
      }
      if (getCandidateExpertiseStatus) {
        const expertiseStatus = getCandidateExpertiseStatus(index);
        if (expertiseStatus.over) {
          errors.push({
            field: `candidate-expertise-${index}`,
            message: `Field of Expertise for ${cr.candidateName} exceeds the ${expertiseStatus.limit} word limit (${expertiseStatus.count} words).`,
            group: 'candidates',
          });
        }
      }
    });

    return errors;
  }, [
    formData,
    getFieldStatus,
    getCandidateStatus,
    getCandidateExpertiseStatus,
    getAttendanceStatus,
  ]);

  const getIncompleteFields = useCallback(() => {
    const warnings = [];

    TEXT_FIELDS.forEach(({ field, label }) => {
      if (!formData[field]?.trim()) {
        warnings.push({
          field,
          message: `${label} is required.`,
        });
      }
    });

    formData.memberAttendance.forEach((member, index) => {
      if (!member.attendance?.trim()) {
        warnings.push({
          field: `attendance-${index}`,
          message: `Attendance for ${member.memberName} is required.`,
          group: 'attendance',
        });
      }
    });

    formData.candidateReports.forEach((cr, index) => {
      if (!cr.report?.trim()) {
        warnings.push({
          field: `candidate-${index}`,
          message: `Report for ${cr.candidateName} is required.`,
          group: 'candidates',
        });
      }
    });

    return warnings;
  }, [formData]);

  const validateAllFields = useCallback(() => {
    const errors = validateFilledFields();

    TEXT_FIELDS.forEach(({ field, label }) => {
      if (!formData[field]?.trim()) {
        errors.push({
          field,
          message: `${label} is required.`,
        });
      }
    });

    formData.memberAttendance.forEach((member, index) => {
      if (!member.attendance?.trim()) {
        errors.push({
          field: `attendance-${index}`,
          message: `Attendance for ${member.memberName} is required.`,
          group: 'attendance',
        });
      }
    });

    formData.candidateReports.forEach((cr, index) => {
      if (!cr.report?.trim()) {
        errors.push({
          field: `candidate-${index}`,
          message: `Report for ${cr.candidateName} is required.`,
          group: 'candidates',
        });
      }
    });

    const seen = new Set();
    return errors.filter((e) => {
      if (seen.has(e.field)) return false;
      seen.add(e.field);
      return true;
    });
  }, [formData, validateFilledFields]);

  const hasFieldError = useCallback(
    (field) =>
      validationErrors.some((e) => e.field === field) ||
      warningErrors.some((e) => e.field === field),
    [validationErrors, warningErrors],
  );

  return {
    validationErrors,
    setValidationErrors,
    warningErrors,
    setWarningErrors,
    isDirty,
    setIsDirty,
    handleFieldChange,
    handleAttendanceChange,
    handleCandidateReportChange,
    handleCandidateExpertiseChange,
    validateFilledFields,
    validateAllFields,
    getIncompleteFields,
    hasFieldError,
  };
};

export default useChairReportForm;
